// Where the problem view stops stacking and puts the chart beside the chat.
//
// Students working on iPads reported having to use one panel at a time. The
// obvious fix is a Tailwind md: breakpoint, and it is wrong: at 768px the two
// COLUMNS fit but the CHART does not. The 400px floor lands on the outer card,
// which spends 26px on p-3 plus its border before the chart sees any of it,
// while InteractiveGraph still clamps its layout to MIN_CHART_WIDTH — so the
// legend and the right edge were being cropped by the card's overflow.
//
// So the cutover is DERIVED from what the two panels actually need, and every
// term below is MEASURED in the running app rather than read off a class name:
//
//   row content   = NARROW_GRAPH_FLOOR + COLUMN_GAP + NARROW_SIDE_FLOOR
//   outer chrome  = viewport - row.clientWidth   (Dashboard + view padding)
//
// These live in their own module because Dashboard caps its scroll container at
// the SAME width: two independent literals would drift apart the next time
// either side is touched, and nothing would notice.

// The chart's real floor, from the module InteractiveGraph actually clamps
// against. NOT re-declared here — a local copy would keep the old value when
// that floor moves, which is precisely the drift this module exists to stop.
import { MIN_CHART_WIDTH } from './chartLayout';

export { MIN_CHART_WIDTH };

/** The graph card's p-3 on both sides plus its 1px border. */
export const GRAPH_CARD_CHROME = 26;

/** Narrow two-column floors: the card must be able to hold a full-width chart. */
export const NARROW_GRAPH_FLOOR = MIN_CHART_WIDTH + GRAPH_CARD_CHROME;
export const NARROW_SIDE_FLOOR = 300;

// The row's computed column-gap. It is INSIDE the row, so it comes out of the
// space the two panels share — an earlier version folded it into OUTER_CHROME
// by mistake and put the cutover 10px too low, which left 790–799 rendering two
// columns whose content overflowed the row by exactly this much.
export const COLUMN_GAP = 10;

// Everything outside the row: Dashboard's padding plus the view's own. The drag
// handle is display:none at these widths and contributes nothing.
//
// This is NOT globally constant — measured, it is 64 below 1024, 79 from 1024
// (Tailwind lg adds padding), and 359 from 1440 where the sidebar becomes
// inline. It only has to be right NEAR THE CUTOVER, which is the one place the
// row has no slack, and it is a flat 64 across 789–1023. Above that the wider
// floors (520 + 360) still leave ~55px spare at 1024, so the larger chrome
// costs nothing. The 799/800 boundary tests are what actually hold this — if a
// layout change moves the chrome down into the cutover band, they go red.
export const OUTER_CHROME = 64;

/** What the ROW must be able to hold for two columns at the narrow floors. */
export const TWO_COLUMN_MIN_ROW_WIDTH =
  NARROW_GRAPH_FLOOR + COLUMN_GAP + NARROW_SIDE_FLOOR;

// From Tailwind's lg breakpoint the drag separator between the panels is
// displayed (`hidden lg:flex`), so the row holds THREE children: two gaps
// plus the handle at its natural width. Measured at 1090px before this was
// accounted for: the handle was squeezed from 10px to 5px to make the row
// fit — no overflow, but a thinner handle exactly at the edge (Codex).
export const LG_BREAKPOINT = 1024;
export const SEPARATOR_WIDTH = 10;
export const TWO_COLUMN_MIN_ROW_WIDTH_LG =
  NARROW_GRAPH_FLOOR + NARROW_SIDE_FLOOR + 2 * COLUMN_GAP + SEPARATOR_WIDTH;

// ProblemIntegrationView decides on its MEASURED row width, not the viewport:
// with the lesson-list sidebar inline (iPad landscape and up) the same viewport
// leaves the row ~300px narrower, and no viewport constant can know which
// layout Dashboard chose. The viewport figure below is kept for the one place
// that cannot measure — Dashboard's compact-mode height cap, a Tailwind
// variant — and equals the row minimum plus the compact-mode chrome.
export const TWO_COLUMN_MIN_VIEWPORT = TWO_COLUMN_MIN_ROW_WIDTH + OUTER_CHROME;

// From this row width the desktop floors (520 / 360) apply instead of the
// narrow ones. 960 is the old "viewport < 1024" rule minus the compact chrome,
// and comfortably holds 520 + 10 + 360 = 890.
export const WIDE_FLOORS_MIN_ROW_WIDTH = 960;

// Below this viewport width Dashboard keeps the lesson list as a full-screen
// overlay and the compact layout; from it, the sidebar is inline like on a
// desktop (students asked for the tablet to work like the PC). Derived from
// what the desktop layout needs to still give the row its minimum.
//
// INLINE_SIDEBAR_CHROME is everything the desktop layout spends outside the
// row: the inline lesson list (renders at 240px below Tailwind xl, 260 from
// 1280 — NOT the 384/416 its max-w classes suggest), the column gap and the
// card padding. Measured in the browser as viewport − row.clientWidth: a flat
// 339 from 1094 through 1279, 359 from 1280 where there is room to spare.
// SCROLLBAR_ALLOWANCE covers a classic 15px scrollbar (desktop browsers; iPad
// overlays its own) so the guarantee holds while the chat is long.
//
// The desktop layout is always at or above lg, so the row minimum that
// applies is the lg one. Two scrollbar gutters can appear at once (the
// Dashboard page and PIV's inner scroll area), so the allowance covers two.
// 756 + 339 + 30 = 1125: iPad landscape (1194) gets the desktop layout with
// ~70px spare; portrait (834) cannot fit three panels at their floors and
// keeps the compact layout with the sidebar as an overlay.
export const INLINE_SIDEBAR_CHROME = 339;
export const SCROLLBAR_ALLOWANCE = 15;
export const DESKTOP_LAYOUT_MIN_VIEWPORT =
  TWO_COLUMN_MIN_ROW_WIDTH_LG + INLINE_SIDEBAR_CHROME + 2 * SCROLLBAR_ALLOWANCE;
